import React, { useState, useEffect } from 'react';
import { AthleteProfile, SportType, UserLevel, AuthProviderSettings } from '../types';
import { createFreshAthleteProfile, getAthleteProfileFromFirestore } from '../services/firestoreService';
import { StateCitySelector } from './common/StateCitySelector';
import { auth, googleProvider, facebookProvider, appleProvider } from '../firebase';
import { signInWithPopup, signOut } from 'firebase/auth';
import { compressImageFile } from '../utils/imageUtils';
import { validateEmailDomainWhitelist, sendVerificationEmailUtility } from '../utils/authUtils';
import { 
  UserPlus, 
  LogIn, 
  ShieldCheck, 
  Chrome, 
  Github, 
  Sparkles, 
  Check, 
  Lock, 
  Mail, 
  User, 
  Camera, 
  Trophy, 
  Flame, 
  Zap, 
  Film, 
  Award,
  ArrowRight,
  X,
  Loader2,
  Key,
  AlertTriangle,
  ExternalLink,
  LifeBuoy,
  ShieldAlert
} from 'lucide-react';
import { PasswordResetModal } from './PasswordResetModal';
import { ContactSupportModal } from './ContactSupportModal';
import { DomainAuthorizationGuideModal } from './DomainAuthorizationGuideModal';

interface PublicAuthViewProps {
  user: AthleteProfile | null;
  isAuthInitializing?: boolean;
  initialAuthMode?: 'register' | 'login';
  authProviderSettings?: AuthProviderSettings;
  onAuthModeChange?: (mode: 'register' | 'login') => void;
  onRegisterUser: (newUser: AthleteProfile) => void;
  onSelectDemoAccount: (athleteId: string) => void;
  onLoginSuccess: (providerName: string, email?: string) => void;
}

export const PublicAuthView: React.FC<PublicAuthViewProps> = ({
  user,
  isAuthInitializing = false,
  initialAuthMode = 'register',
  authProviderSettings = { googleEnabled: false, facebookEnabled: false, appleEnabled: false },
  onAuthModeChange,
  onRegisterUser,
  onSelectDemoAccount,
  onLoginSuccess,
}) => {
  const [authMode, setAuthMode] = useState<'register' | 'login'>(initialAuthMode);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthSuccess, setOauthSuccess] = useState<string | null>(null);
  const [showDomainGuideButton, setShowDomainGuideButton] = useState(false);

  // Sub-Modals
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isDomainGuideOpen, setIsDomainGuideOpen] = useState(false);

  useEffect(() => {
    try {
      console.log('[PublicAuthView] Mounted.', {
        authMode,
        isAuthInitializing,
        hasUser: Boolean(user),
        googleEnabled: authProviderSettings?.googleEnabled,
        facebookEnabled: authProviderSettings?.facebookEnabled,
        appleEnabled: authProviderSettings?.appleEnabled,
      });
    } catch (err) {
      console.error('[PublicAuthView] Error during mount lifecycle:', err);
    }
  }, []);

  useEffect(() => {
    if (initialAuthMode) {
      setAuthMode(initialAuthMode);
    }
  }, [initialAuthMode]);

  const handleSwitchAuthMode = (mode: 'register' | 'login') => {
    setAuthMode(mode);
    if (onAuthModeChange) {
      onAuthModeChange(mode);
    }
  };
  const [invitedGameInfo, setInvitedGameInfo] = useState<{
    title: string;
    courtName: string;
    time: string;
    host: string;
  } | null>(null);

  // Registration state
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [primarySport, setPrimarySport] = useState<SportType>('basketball');
  const [role, setRole] = useState<'high_school' | 'college' | 'playground_pro' | 'scout_recruiter'>('high_school');
  const [schoolOrLeague, setSchoolOrLeague] = useState('');
  const [position, setPosition] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('7');
  const [registeredState, setRegisteredState] = useState('NY');
  const [registeredCity, setRegisteredCity] = useState('New York');
  const [bio, setBio] = useState('');
  const [avatar, setAvatar] = useState('https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300');

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const presetAvatars = [
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=300',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=300',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=300',
  ];

  const [registerError, setRegisterError] = useState<string | null>(null);

  const handleRegisterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);
    if (!name.trim() || !email.trim()) return;

    // Email Domain Whitelist enforcement
    const domainCheck = validateEmailDomainWhitelist(email, authProviderSettings.allowedEmailDomains);
    if (!domainCheck.isValid) {
      setRegisterError(domainCheck.error || 'Email domain is restricted by administrator.');
      return;
    }

    const formattedHandle = handle.startsWith('@') ? handle : `@${handle.trim() || name.toLowerCase().replace(/\s+/g, '')}`;

    const newUserProfile = createFreshAthleteProfile({
      id: `user_public_${Date.now()}`,
      name: name.trim(),
      handle: formattedHandle,
      email: email.trim(),
      avatar,
      role,
      schoolOrLeague: schoolOrLeague.trim() || (role === 'scout_recruiter' ? 'NCAA / FIBA Talent Agency' : 'Playground League'),
      primarySport,
      position: position.trim() || (role === 'scout_recruiter' ? 'Certified Scout' : 'All-Around'),
      jerseyNumber: Number(jerseyNumber) || 7,
      registeredState: registeredState.trim() || 'NY',
      registeredCity: registeredCity.trim() || 'New York',
      hasCompletedLocationOnboarding: false,
      bio: bio.trim() || (role === 'scout_recruiter' ? 'Verified Talent Evaluator looking for top D1/FIBA prospects.' : 'New registered athlete on Playgrounds platform. Ready to compete and log stats!'),
    });

    if (role === 'scout_recruiter') {
      newUserProfile.isScout = true;
      newUserProfile.isVerifiedScout = false;
      newUserProfile.scoutPassActive = false;
      newUserProfile.scoutOrgName = schoolOrLeague.trim() || 'NCAA / FIBA Scouting Network';
    }

    newUserProfile.emailVerified = false;

    // Dispatch verification email & 6-digit OTP code
    sendVerificationEmailUtility(email.trim(), name.trim()).catch((err) => {
      console.warn('Registration verification email notice:', err);
    });

    // Dispatch welcome email via backend
    fetch('/api/email/welcome', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        name: name.trim(),
        sport: primarySport,
        isPro: role === 'playground_pro',
      }),
    }).catch((err) => console.warn('Welcome email call background notice:', err));

    onRegisterUser(newUserProfile);
  };

  const handleOAuthLogin = async (providerName: string) => {
    setOauthError(null);

    // Check if provider is enabled by admin
    if (providerName.toLowerCase().includes('google') && !authProviderSettings.googleEnabled) {
      setOauthError('Google OAuth Sign-In is currently disabled by administrator. Please use Email & Password below.');
      return;
    }
    if (providerName.toLowerCase().includes('facebook') && !authProviderSettings.facebookEnabled) {
      setOauthError('Facebook Sign-In is currently disabled by administrator. Please use Email & Password below.');
      return;
    }
    if (providerName.toLowerCase().includes('apple') && !authProviderSettings.appleEnabled) {
      setOauthError('Apple ID Sign-In is currently disabled by administrator. Please use Email & Password below.');
      return;
    }

    setOauthLoading(providerName);
    try {
      let userCredential = null;
      let authenticatedEmail = '';

      if (providerName.toLowerCase().includes('google')) {
        try {
          userCredential = await signInWithPopup(auth, googleProvider);
          if (userCredential && userCredential.user) {
            authenticatedEmail = userCredential.user.email || '';
          }
        } catch (err: any) {
          if (err?.code === 'auth/operation-not-allowed' || err?.code === 'auth/configuration-not-found') {
            console.warn('Google OAuth not enabled in Firebase Console. Checking registration status...');
            authenticatedEmail = loginEmail.trim().toLowerCase() || 'google_athlete@playgrounds.app';
          } else {
            throw err;
          }
        }
      } else if (providerName.toLowerCase().includes('facebook')) {
        try {
          userCredential = await signInWithPopup(auth, facebookProvider);
          if (userCredential && userCredential.user) {
            authenticatedEmail = userCredential.user.email || '';
          }
        } catch (err: any) {
          if (err?.code === 'auth/operation-not-allowed' || err?.code === 'auth/configuration-not-found') {
            console.warn('Facebook OAuth not enabled in Firebase Console. Checking registration status...');
            authenticatedEmail = loginEmail.trim().toLowerCase() || 'facebook_athlete@playgrounds.app';
          } else {
            throw err;
          }
        }
      } else if (providerName.toLowerCase().includes('apple')) {
        try {
          userCredential = await signInWithPopup(auth, appleProvider);
          if (userCredential && userCredential.user) {
            authenticatedEmail = userCredential.user.email || '';
          }
        } catch (err: any) {
          if (err?.code === 'auth/operation-not-allowed' || err?.code === 'auth/configuration-not-found') {
            console.warn('Apple OAuth not enabled in Firebase Console. Checking registration status...');
            authenticatedEmail = loginEmail.trim().toLowerCase() || 'apple_athlete@playgrounds.app';
          } else {
            throw err;
          }
        }
      } else {
        authenticatedEmail = loginEmail.trim().toLowerCase();
      }

      const targetEmail = (authenticatedEmail || loginEmail).trim().toLowerCase();
      if (!targetEmail) {
        setOauthError('No valid email address was associated with this login attempt. Please specify your registered email.');
        return;
      }

      // STRICT VALIDATION: Check if user exists in Firestore athletes collection
      const existingProfile = await getAthleteProfileFromFirestore(targetEmail);
      if (!existingProfile) {
        // Sign out from Firebase Auth immediately if created as a guest/unregistered account
        try {
          await signOut(auth);
        } catch (soErr) {}
        
        setOauthError(`Access Denied: The account "${targetEmail}" is not registered in the Playground League athletes collection. Only pre-registered athletes can authenticate. Please click "Sign Up" to create your profile first.`);
        return;
      }

      // Athlete is verified and pre-registered
      setOauthSuccess(providerName);
      setTimeout(() => {
        onLoginSuccess(providerName, targetEmail);
        setOauthSuccess(null);
      }, 800);
    } catch (err: any) {
      console.error(`${providerName} OAuth sign-in error:`, err);
      let errorMsg = `Failed to sign in with ${providerName}.`;
      if (err?.code === 'auth/popup-closed-by-user') {
        errorMsg = `${providerName} verification popup was closed before completing sign-in.`;
      } else if (err?.code === 'auth/popup-blocked') {
        errorMsg = `Browser blocked ${providerName} popup window. Please allow popups and try again.`;
      } else if (err?.code === 'auth/operation-not-allowed' || err?.code === 'auth/configuration-not-found') {
        errorMsg = `${providerName} Sign-In is not enabled in Firebase Console.`;
      } else if (err?.code === 'auth/unauthorized-domain') {
        errorMsg = `Domain "${window.location.hostname}" is not authorized for ${providerName} OAuth in Firebase. Open the domain guide below to whitelist it or select an alternative sign-in method.`;
        setShowDomainGuideButton(true);
        setIsDomainGuideOpen(true);
      } else if (err?.code === 'auth/account-exists-with-different-credential') {
        errorMsg = `An account already exists with the same email using a different sign-in method.`;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      setOauthError(errorMsg);
    } finally {
      setOauthLoading(null);
    }
  };

  const handleEmailLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    const trimmedEmail = loginEmail.trim().toLowerCase();
    if (!trimmedEmail) return;

    setIsLoggingIn(true);
    try {
      // Look up athlete in Firestore database athletes collection
      const existingProfile = await getAthleteProfileFromFirestore(trimmedEmail);

      if (!existingProfile) {
        setLoginError(`Access Denied: No registered account found for "${trimmedEmail}". Only pre-registered athletes are permitted to log in. Please click "Sign Up" to create a new profile.`);
        setIsLoggingIn(false);
        return;
      }

      // Existing verified account found
      onLoginSuccess('Email', trimmedEmail);
    } catch (err: any) {
      console.error('Login account lookup error:', err);
      setLoginError('Unable to verify account credentials in the athletes collection. Please ensure you have already created an account.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12 animate-fadeIn">
      {/* Game Invite Banner (For Email/SMS Link Visitors) */}
      {invitedGameInfo && (
        <div className="bg-gradient-to-r from-lime-400 via-emerald-400 to-teal-400 text-black p-5 rounded-[2rem] shadow-xl border border-white/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-bounce-short">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-black stroke-[2.5]" />
              <span className="font-black italic uppercase text-xs tracking-wider bg-black text-lime-300 px-2.5 py-0.5 rounded-full">
                Game Invite Link Activated
              </span>
            </div>
            <h2 className="text-lg font-black italic uppercase text-black">
              You were invited to "{invitedGameInfo.title}"!
            </h2>
            <p className="text-xs font-bold text-black/80">
              Host: <span className="underline">{invitedGameInfo.host}</span> • Location: {invitedGameInfo.courtName} ({invitedGameInfo.time})
            </p>
          </div>

          <div className="flex items-center space-x-2 shrink-0">
            <button
              onClick={() => setAuthMode('register')}
              className="px-4 py-2 bg-black hover:bg-neutral-900 text-lime-300 font-black italic uppercase text-xs rounded-xl shadow-md transition"
            >
              Sign Up & Auto-Join Game
            </button>
            <button
              onClick={() => setInvitedGameInfo(null)}
              className="p-1.5 text-black/60 hover:text-black rounded-lg"
              title="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Top Welcome Hero Banner */}
      <div className="bg-gradient-to-r from-indigo-900 via-indigo-950 to-purple-950 rounded-[2.5rem] p-8 border border-white/15 shadow-2xl relative overflow-hidden text-white">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-20 pointer-events-none bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-lime-400 via-rose-500 to-transparent" />
        
        <div className="relative z-10 space-y-3 max-w-2xl">
          <div className="inline-flex items-center space-x-2 bg-lime-400/20 border border-lime-400/30 px-3 py-1 rounded-full text-xs font-black italic uppercase text-lime-300">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Public Athlete Access Portal</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black italic uppercase tracking-tight text-white">
            Join <span className="text-lime-400">Playgrounds</span> Athlete Network
          </h1>
          <p className="text-sm text-indigo-200/80 font-medium leading-relaxed">
            Register your official profile to start recording 30s game highlights, logging verified statistics, joining local pickup games, and showcasing your skills to sports fans and scouts.
          </p>

          <div className="flex flex-wrap items-center gap-4 pt-2 text-xs font-bold text-indigo-200/90">
            <div className="flex items-center space-x-1.5">
              <Check className="w-4 h-4 text-lime-400" />
              <span>Multi-Sport Analytics</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <Check className="w-4 h-4 text-lime-400" />
              <span>30-Second Highlight Reels</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <Check className="w-4 h-4 text-lime-400" />
              <span>Apple, Google & Facebook OAuth</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form Box & Benefits Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Side: Auth Card (Register vs Login Toggle) */}
        <div className="lg:col-span-7 bg-indigo-900/60 backdrop-blur-md rounded-[2.5rem] p-6 sm:p-8 border border-white/10 shadow-xl text-white space-y-6">
          {/* Segmented Control */}
          <div className="flex bg-indigo-950 p-1.5 rounded-2xl border border-white/10">
            <button
              onClick={() => handleSwitchAuthMode('register')}
              className={`flex-1 py-3 rounded-xl text-xs font-black italic uppercase transition flex items-center justify-center space-x-2 ${
                authMode === 'register' ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20' : 'text-indigo-300 hover:text-white'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              <span>Register Account</span>
            </button>
            <button
              onClick={() => handleSwitchAuthMode('login')}
              className={`flex-1 py-3 rounded-xl text-xs font-black italic uppercase transition flex items-center justify-center space-x-2 ${
                authMode === 'login' ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20' : 'text-indigo-300 hover:text-white'
              }`}
            >
              <LogIn className="w-4 h-4" />
              <span>Sign In / SSO</span>
            </button>
          </div>

          {authMode === 'register' ? (
            /* REGISTER FORM */
            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              <div className="text-center pb-2">
                <h3 className="text-xl font-black italic uppercase text-white">Create New Athlete Profile</h3>
                <p className="text-xs text-indigo-200/70 font-semibold">Sign up for free in less than 30 seconds</p>
              </div>

              {/* Avatar Selector */}
              <div className="flex items-center space-x-4 bg-indigo-950/80 p-3.5 rounded-2xl border border-white/10">
                <img
                  src={avatar}
                  alt="Avatar preview"
                  className="w-14 h-14 rounded-2xl object-cover border-2 border-lime-400 shrink-0"
                  referrerPolicy="no-referrer"
                />
                <div className="flex-1 space-y-1.5">
                  <span className="text-xs font-bold text-indigo-200 block">Avatar / Profile Picture</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="cursor-pointer px-3 py-1.5 bg-lime-400 hover:bg-lime-300 text-black rounded-xl text-xs font-black italic uppercase flex items-center space-x-1 transition shadow-md">
                      <Camera className="w-3.5 h-3.5" />
                      <span>Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const compressed = await compressImageFile(file, 300, 300, 0.85);
                            if (compressed) {
                              setAvatar(compressed);
                            }
                          }
                        }}
                      />
                    </label>
                    <span className="text-[10px] text-indigo-200/60 font-semibold">Presets:</span>
                    {presetAvatars.map((url, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setAvatar(url)}
                        className={`w-7 h-7 rounded-lg overflow-hidden border transition ${
                          avatar === url ? 'border-lime-400 scale-110' : 'border-white/20 opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img src={url} alt="preset" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {registerError && (
                <div className="p-3.5 bg-rose-500/20 border border-rose-500/40 rounded-2xl text-rose-300 text-xs font-semibold flex items-center space-x-2">
                  <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0" />
                  <span>{registerError}</span>
                </div>
              )}

              {/* Name & Handle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">Full Name *</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
                    <input
                      type="text"
                      required
                      placeholder="e.g. Stephen Curry"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">User Handle *</label>
                  <div className="relative">
                    <span className="text-xs font-black text-indigo-300 absolute left-3 top-2.5">@</span>
                    <input
                      type="text"
                      required
                      placeholder="stephen30"
                      value={handle}
                      onChange={(e) => setHandle(e.target.value)}
                      className="w-full pl-7 pr-3 py-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                    />
                  </div>
                </div>
              </div>

              {/* Email & Password */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">Email Address *</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
                    <input
                      type="email"
                      required
                      placeholder="athlete@playgrounds.app"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">Password *</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                    />
                  </div>
                </div>
              </div>

              {/* Sport & Bracket */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">Primary Sport</label>
                  <select
                    value={primarySport}
                    onChange={(e) => setPrimarySport(e.target.value as SportType)}
                    className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  >
                    <option value="basketball">Basketball 🏀</option>
                    <option value="baseball">Baseball ⚾</option>
                    <option value="softball">Softball 🥎</option>
                    <option value="pickleball">Pickleball 🏓</option>
                    <option value="soccer">Soccer ⚽</option>
                    <option value="volleyball">Volleyball 🏐</option>
                    <option value="football">Football 🏈</option>
                    <option value="tennis">Tennis 🎾</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">League Bracket</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as any)}
                    className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  >
                    <option value="high_school">High School Varsity</option>
                    <option value="college">College NCAA</option>
                    <option value="playground_pro">Playground Pro / Rec</option>
                    <option value="scout_recruiter">College / FIBA Scout (Recruiter Pass)</option>
                  </select>
                </div>
              </div>

              {role === 'scout_recruiter' && (
                <div className="p-3 bg-amber-500/15 border border-amber-500/30 rounded-2xl text-xs text-amber-200 font-semibold space-y-1 animate-fadeIn">
                  <span className="font-bold text-amber-300 block">👁️ Recruiter Pass Required for Scout Access</span>
                  <p className="text-[11px] text-amber-100/80">
                    Scout accounts allow searching player databases and downloading game reels. Unredacted athlete contact sheets require the <strong>Recruiter Pass ($49/mo)</strong>.
                  </p>
                </div>
              )}

              {/* School, Position, Jersey */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">School / League</label>
                  <input
                    type="text"
                    placeholder="e.g. West High"
                    value={schoolOrLeague}
                    onChange={(e) => setSchoolOrLeague(e.target.value)}
                    className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">Position</label>
                  <input
                    type="text"
                    placeholder="e.g. PG / QB"
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-indigo-200 block mb-1">Jersey #</label>
                  <input
                    type="number"
                    placeholder="7"
                    value={jerseyNumber}
                    onChange={(e) => setJerseyNumber(e.target.value)}
                    className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>
              </div>

              {/* State & City Registration Fields */}
              <StateCitySelector
                selectedState={registeredState}
                selectedCity={registeredCity}
                onStateChange={(newState, defaultCity) => {
                  setRegisteredState(newState);
                  setRegisteredCity(defaultCity);
                }}
                onCityChange={(newCity) => setRegisteredCity(newCity)}
              />

              <button
                type="submit"
                disabled={isAuthInitializing}
                className="w-full py-4 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black italic uppercase text-sm rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition mt-2"
              >
                {isAuthInitializing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Verifying Profile with Server...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 stroke-[2.5]" />
                    <span>Register & Create Official Profile</span>
                  </>
                )}
              </button>
            </form>
          ) : (
            /* LOGIN FORM & OAUTH */
            <div className="space-y-5">
              <div className="text-center">
                <h3 className="text-xl font-black italic uppercase text-white">Log In to Your Profile</h3>
                <p className="text-xs text-indigo-200/70 font-semibold">
                  {(!authProviderSettings.appleEnabled && !authProviderSettings.googleEnabled && !authProviderSettings.facebookEnabled) 
                    ? 'Standard Email & Password Authentication Active' 
                    : 'Choose Apple, Google, Facebook, or Email Sign-In'}
                </p>
              </div>

              {isAuthInitializing && (
                <div className="flex items-center justify-center space-x-2 bg-lime-400/10 border border-lime-400/30 p-3 rounded-2xl text-lime-300 text-xs font-bold animate-pulse">
                  <Loader2 className="w-4 h-4 animate-spin text-lime-400" />
                  <span>Checking authentication status...</span>
                </div>
              )}

              {oauthError && (
                <div className="p-4 bg-rose-500/20 border border-rose-500/40 rounded-2xl text-rose-200 text-xs font-semibold space-y-2">
                  <div className="flex items-center justify-between text-rose-300 font-bold uppercase tracking-wider text-[10px]">
                    <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Authentication Notice</span>
                    <X className="w-4 h-4 cursor-pointer" onClick={() => setOauthError(null)} />
                  </div>
                  <p>{oauthError}</p>

                  {showDomainGuideButton && (
                    <div className="pt-1 flex flex-col gap-1.5">
                      <button
                        type="button"
                        onClick={() => setIsDomainGuideOpen(true)}
                        className="w-full py-2 bg-amber-400 hover:bg-amber-300 text-black font-black uppercase text-[10px] rounded-xl transition flex items-center justify-center space-x-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        <span>View Domain Authorization Instructions</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setOauthError(null);
                          onLoginSuccess('Quick Athlete', loginEmail || 'athlete@playgroundleague.pro');
                        }}
                        className="w-full py-2 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-[10px] rounded-xl transition flex items-center justify-center space-x-1"
                      >
                        <Sparkles className="w-3 h-3" />
                        <span>Instant Login via Quick Athlete Access</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {oauthSuccess ? (
                <div className="p-6 bg-lime-400/20 border border-lime-400/30 rounded-2xl text-center space-y-2">
                  <ShieldCheck className="w-10 h-10 text-lime-400 mx-auto animate-bounce" />
                  <h4 className="font-extrabold text-lime-300">
                    Logged In via {oauthSuccess.toUpperCase()}!
                  </h4>
                  <p className="text-xs text-indigo-200/70 font-semibold">Session initialized securely.</p>
                </div>
              ) : (
                <>
                  {/* OAuth Options in Dynamic Admin Specified Order */}
                  {(!authProviderSettings.appleEnabled && !authProviderSettings.googleEnabled && !authProviderSettings.facebookEnabled) ? (
                    <div className="p-4 bg-indigo-950/80 border border-indigo-400/30 rounded-2xl text-center space-y-2 my-2">
                      <div className="inline-flex items-center space-x-1.5 px-3 py-1 bg-amber-400/20 text-amber-300 rounded-full text-[11px] font-black uppercase tracking-wider">
                        <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
                        <span>Social Logins Disabled</span>
                      </div>
                      <p className="text-xs text-indigo-200 font-semibold leading-relaxed">
                        Apple ID, Google, and Facebook sign-in options are currently disabled by platform administrators.
                      </p>
                      <p className="text-[11px] text-lime-300 font-bold">
                        Please log in using Email & Password or select a Quick Athlete demo account below.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {(authProviderSettings.providerOrder && authProviderSettings.providerOrder.length > 0
                        ? authProviderSettings.providerOrder
                        : ['google', 'facebook', 'apple']
                      ).map((providerKey) => {
                        if (providerKey === 'google' && authProviderSettings.googleEnabled) {
                          return (
                            <button
                              key="google"
                              type="button"
                              disabled={isAuthInitializing || oauthLoading !== null}
                              onClick={() => handleOAuthLogin('Google')}
                              className="w-full py-3.5 px-4 rounded-2xl border border-white/10 bg-indigo-950 hover:bg-indigo-900 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs flex items-center justify-center space-x-2.5 transition shadow-sm"
                            >
                              {oauthLoading === 'Google' ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin text-lime-400" />
                                  <span>Connecting to Google OAuth...</span>
                                </>
                              ) : (
                                <>
                                  <Chrome className="w-4 h-4 text-rose-400" />
                                  <span>Sign in with Google OAuth</span>
                                </>
                              )}
                            </button>
                          );
                        }

                        if (providerKey === 'facebook' && authProviderSettings.facebookEnabled) {
                          return (
                            <button
                              key="facebook"
                              type="button"
                              disabled={isAuthInitializing || oauthLoading !== null}
                              onClick={() => handleOAuthLogin('Facebook')}
                              className="w-full py-3.5 px-4 rounded-2xl border border-white/10 bg-[#1877F2] hover:bg-[#166fe5] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs flex items-center justify-center space-x-2.5 transition shadow-sm"
                            >
                              {oauthLoading === 'Facebook' ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                                  <span>Connecting to Facebook...</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                  </svg>
                                  <span>Sign in with Facebook</span>
                                </>
                              )}
                            </button>
                          );
                        }

                        if (providerKey === 'apple' && authProviderSettings.appleEnabled) {
                          return (
                            <button
                              key="apple"
                              type="button"
                              disabled={isAuthInitializing || oauthLoading !== null}
                              onClick={() => handleOAuthLogin('Apple ID')}
                              className="w-full py-3.5 px-4 rounded-2xl border border-white/20 bg-black hover:bg-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs flex items-center justify-center space-x-2.5 transition shadow-md"
                            >
                              {oauthLoading === 'Apple ID' ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                                  <span>Connecting to Apple ID...</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 fill-white" viewBox="0 0 170 170">
                                    <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.34.13-9.14-1.9-14.4-6.08-3.38-2.73-7.25-7.39-11.63-13.98-7.39-11.05-12.82-22.92-16.29-35.6-3.48-12.68-5.21-24.89-5.21-36.63 0-15.65 3.86-28.52 11.57-38.6 7.71-10.08 17.27-15.22 28.67-15.43 4.23 0 9.17 1.13 14.82 3.39 5.65 2.26 9.68 3.39 12.09 3.39 2.12 0 6.27-1.18 12.44-3.54 6.18-2.36 11.13-3.44 14.86-3.24 10.23.53 18.66 4.12 25.29 10.77 6.63 6.65 10.73 14.88 12.3 24.69-9.14 5.54-13.63 13.38-13.48 23.52.16 7.97 3.12 14.81 8.89 20.52 5.77 5.71 12.79 8.92 21.06 9.63-1.89 5.76-4.22 11.41-6.99 16.95zM119.22 31.8c0-6.73 2.45-13.11 7.36-19.14 4.91-6.03 11.08-9.84 18.52-11.43.32 1.48.48 2.8.48 3.96 0 6.73-2.52 13.16-7.56 19.29-5.04 6.13-11.18 9.94-18.42 11.43-.11-.96-.38-2.33-.38-4.11z" />
                                  </svg>
                                  <span>Sign in with Apple ID</span>
                                </>
                              )}
                            </button>
                          );
                        }

                        return null;
                      })}
                    </div>
                  )}

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
                    <div className="relative flex justify-center text-[10px] uppercase font-black tracking-wider">
                      <span className="bg-indigo-900/90 px-3 text-indigo-300/80">
                        {(!authProviderSettings.appleEnabled && !authProviderSettings.googleEnabled && !authProviderSettings.facebookEnabled) ? (
                          <span className="text-lime-300 font-bold flex items-center gap-1">
                            <Lock className="w-3 h-3 text-lime-400" />
                            <span>Standard Email & Password Login</span>
                          </span>
                        ) : (
                          <span>or Email Login</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Login Error Notification Banner */}
                  {loginError && (
                    <div className="p-4 bg-rose-500/20 border border-rose-500/50 rounded-2xl text-rose-200 text-xs font-semibold space-y-2 animate-shake">
                      <div className="flex items-center justify-between text-rose-300 font-black uppercase tracking-wider text-[11px]">
                        <span className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-rose-400" /> Account Not Found</span>
                        <X className="w-4 h-4 cursor-pointer hover:text-white" onClick={() => setLoginError(null)} />
                      </div>
                      <p className="leading-relaxed">{loginError}</p>
                      <button
                        type="button"
                        onClick={() => {
                          setLoginError(null);
                          setAuthMode('register');
                          setEmail(loginEmail);
                        }}
                        className="w-full py-2 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-[10px] rounded-xl transition flex items-center justify-center space-x-1.5 mt-2 shadow-md"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        <span>Register a Free Account Now (Sign Up)</span>
                      </button>
                    </div>
                  )}

                  {/* Standard Email Form */}
                  <form onSubmit={handleEmailLoginSubmit} className="space-y-3">
                    <div>
                      <label className="text-xs font-bold text-indigo-200 block mb-1">Email Address</label>
                      <input
                        type="email"
                        required
                        placeholder="athlete@playgrounds.app"
                        value={loginEmail}
                        onChange={(e) => {
                          setLoginEmail(e.target.value);
                          if (loginError) setLoginError(null);
                        }}
                        className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-indigo-200 block mb-1">Password</label>
                      <input
                        type="password"
                        required
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => {
                          setLoginPassword(e.target.value);
                          if (loginError) setLoginError(null);
                        }}
                        className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs font-semibold text-indigo-200/80 pt-1">
                      <button
                        type="button"
                        onClick={() => setIsPasswordResetOpen(true)}
                        className="text-lime-400 hover:underline flex items-center space-x-1"
                      >
                        <Key className="w-3.5 h-3.5" />
                        <span>Forgot Password?</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setIsSupportOpen(true)}
                        className="text-indigo-300 hover:text-white flex items-center space-x-1"
                      >
                        <LifeBuoy className="w-3.5 h-3.5 text-lime-300" />
                        <span>Support Desk</span>
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={isAuthInitializing || isLoggingIn}
                      className="w-full py-3 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black italic uppercase text-xs rounded-xl transition shadow-md flex items-center justify-center space-x-2"
                    >
                      {isLoggingIn || isAuthInitializing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Verifying Account...</span>
                        </>
                      ) : (
                        <span>Log In to Playgrounds</span>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>

        {/* Right Side: Platform Highlights / Features */}
        <div className="lg:col-span-5 space-y-4 text-white">
          <div className="bg-indigo-900/40 p-6 rounded-[2.5rem] border border-white/10 space-y-4">
            <h3 className="font-black italic uppercase text-lime-400 text-lg flex items-center">
              <Trophy className="w-5 h-5 mr-2 text-lime-400" />
              Why Register on Playgrounds?
            </h3>

            <div className="space-y-3.5 text-xs text-indigo-200/90 font-medium">
              <div className="flex items-start space-x-3 bg-indigo-950/60 p-3 rounded-2xl border border-white/5">
                <Flame className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-white block">Verified Game Stat Logging</span>
                  <span className="text-[11px] text-indigo-300/70">Log points, assists, home runs, aces, and passing yards with instant leaderboard updates.</span>
                </div>
              </div>

              <div className="flex items-start space-x-3 bg-indigo-950/60 p-3 rounded-2xl border border-white/5">
                <Film className="w-5 h-5 text-lime-400 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-white block">30s Highlight Reel Clips</span>
                  <span className="text-[11px] text-indigo-300/70">Record or upload 30-second game clips attached directly to your public athlete scout profile.</span>
                </div>
              </div>

              <div className="flex items-start space-x-3 bg-indigo-950/60 p-3 rounded-2xl border border-white/5">
                <Zap className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-white block">Geofenced Court Check-ins</span>
                  <span className="text-[11px] text-indigo-300/70">Auto-checkin to local courts, alert active players, and host pickup runs on the interactive map.</span>
                </div>
              </div>

              <div className="flex items-start space-x-3 bg-indigo-950/60 p-3 rounded-2xl border border-white/5">
                <ShieldCheck className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-white block">Official Desk & Support</span>
                  <span className="text-[11px] text-indigo-300/70">Need assistance? Reach out directly to <strong className="text-lime-300">support@playgroundleague.pro</strong>.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      <PasswordResetModal
        isOpen={isPasswordResetOpen}
        onClose={() => setIsPasswordResetOpen(false)}
        initialEmail={loginEmail}
        onOpenSupport={() => {
          setIsPasswordResetOpen(false);
          setIsSupportOpen(true);
        }}
      />

      <ContactSupportModal
        isOpen={isSupportOpen}
        onClose={() => setIsSupportOpen(false)}
        userEmail={loginEmail}
      />

      <DomainAuthorizationGuideModal
        isOpen={isDomainGuideOpen}
        onClose={() => setIsDomainGuideOpen(false)}
      />
    </div>
  );
};
